import { uniqBy } from 'lodash'
import { type TFunction } from 'react-i18next'
import { ERROR_MSG_2_I18N_KEY, EXCLUDE_UNIT_FAILREASON, NEED_SHOW_ERROR_MESSAGE } from '../Index/constant'
import { ORIGIN_ERROR } from '@/constants'
import type { OutboundsGetRes } from '@/services/api/order/type'

interface TransformFn<R> {
  <
    FailData extends {
      msg?: string
      action?: string
      actions?: string[]
      storageTypes?: number[]
    },
    OutboundFailInfo extends {
      failReason?: string
      failData?: FailData
    }
  >(
    failInfo: OutboundFailInfo,
    t?: TFunction,
    detail?: OutboundsGetRes
  ): R
}

/**
 * 是否显示上游错误信息
 * @param failInfo
 * @returns
 */
export const hasErrorMsg: TransformFn<boolean> = (failInfo) => {
  return failInfo?.failData?.action === ORIGIN_ERROR || NEED_SHOW_ERROR_MESSAGE.includes(failInfo?.failReason)
}

export const getDescription: TransformFn<string | string[]> = (outboundFailInfo, t, detail) => {
  const i18nAction = `enums.outboundFailReason.${outboundFailInfo?.failReason}.${outboundFailInfo?.failData?.action}`
  let i18nTips = `enums.outboundFailReason.tips.${outboundFailInfo?.failReason}`
  // 系统订单 且 是物流无法识别, 如合单场景
  if (detail?.source === 'ONEWAREHOUSE' && 'unknown_logistics' === outboundFailInfo?.failReason) {
    i18nTips = undefined
  }
  const i18nTips2 = `enums.outboundFailReason.Tips.${outboundFailInfo?.failReason}`
  // REMOTE_ERROR 的错误
  const i18nMsg = ERROR_MSG_2_I18N_KEY[outboundFailInfo?.failData?.msg] ?? ''
  const i18nKeyList = [i18nAction, i18nTips, i18nTips2, i18nMsg].filter((v) => v)
  // 多actions的场景
  if (outboundFailInfo?.failData?.actions?.length > 0) {
    return outboundFailInfo?.failData?.actions.map((i) => t(`enums.outboundFailReason.${outboundFailInfo?.failReason}.${i}`))
  }
  for (const key of i18nKeyList) {
    const i18nValue = t([key, ''], { ...outboundFailInfo?.failData })
    if (i18nValue) {
      return i18nValue
    }
  }
  return hasErrorMsg(outboundFailInfo) ? outboundFailInfo?.failData?.msg ?? '' : ''
}

export const getFailInfoRenderObject: TransformFn<{ title?: string; description?: string | string[] }> = (outboundFailInfo, t, detail) => {
  return {
    title: t(`enums.outboundFailReason.${outboundFailInfo.failReason}`),
    description: getDescription(outboundFailInfo, t, detail),
  }
}

const REFUND_FAIL_REASON_TIPS_IN_I18N = ['wallet_balance_not_enough', 'freight_calculate_failed', 'inbound_create_sku_storage_type_not_match', 'basic_data_error']

const REFUND_FAIL_REASON_TIPS_IN_ORIGIN = ['logistics_no_or_waybill_error', 'inbound_audit_fail']

export const getRefundFailInfoRenderObject: TransformFn<{ title?: string; description?: string | string[]; tooltips: string }> = (outboundFailInfo, t, detail) => {
  let tips = null
  const failReason = outboundFailInfo.failReason
  const failData = outboundFailInfo.failData
  if (REFUND_FAIL_REASON_TIPS_IN_I18N.includes(failReason)) {
    if (failReason === 'inbound_create_sku_storage_type_not_match') {
      // enums.storageType.4
      tips = t(`enums.outboundFailReason.${failReason}.tips`, { storageTypes: failData?.storageTypes.map((item) => t(`enums.storageType.${item}`)).join('/') })
    } else {
      tips = t(`enums.outboundFailReason.${failReason}.tips`)
    }
  }

  if (REFUND_FAIL_REASON_TIPS_IN_ORIGIN.includes(failReason)) {
    tips = outboundFailInfo?.failData?.msg
  }

  return {
    title: t(`enums.outboundFailReason.title.${failReason}`),
    tooltips: tips,
    description: getDescription(outboundFailInfo, t, detail),
  }
}

export const getFailInfo = <
  FailData extends {
    msg?: string
    action?: string
  },
  OutboundFailInfo extends {
    failReason?: string
    failData?: FailData
  }
>(
  outboundFailInfos: OutboundFailInfo[] | undefined | null,
  t: TFunction,
  detail?: OutboundsGetRes
) => {
  const noNeedToUniqFailReason = outboundFailInfos?.filter((i) => EXCLUDE_UNIT_FAILREASON.includes(i?.failReason)) || []
  const needToUniqFailReason = outboundFailInfos?.filter((i) => !EXCLUDE_UNIT_FAILREASON.includes(i?.failReason)) || []
  return uniqBy(needToUniqFailReason ?? [], 'failReason')
    ?.concat(noNeedToUniqFailReason)
    ?.map((cur) => {
      const { title, description } = getFailInfoRenderObject(cur, t, detail)
      if (description) {
        return `${title} (${description})`
      }
      return title
    })
}
